import { PARAM_NAMES } from "./params.js";
import { MAX_PATCH_OPS } from "./patch-op.js";
/**
 * Serialize a complete PresetModel into a deterministic PatchOp list.
 * Order: meta → PARAM_NAMES → modulators → legacy lfo_shape gaps →
 * wavetables → sequences → macros → modulations (non-sequence-owned).
 */
export function modelToOps(model) {
    const ops = [];
    ops.push({
        op: "meta",
        presetName: model.name,
        style: model.style,
        ...(model.comments ? { comments: model.comments } : {}),
    });
    for (const param of PARAM_NAMES) {
        ops.push({ op: "set", param, value: model.params[param] });
    }
    const modulators = [...(model.modulators ?? [])]
        .filter((m) => m.ownedBySequence == null)
        .sort((a, b) => a.index - b.index);
    for (const mod of modulators) {
        const freqKey = `lfo${mod.index}_frequency`;
        const timing = mod.timing.mode === "free_hz" && freqKey in model.params
            ? { mode: "free_hz", frequencyHz: model.params[freqKey] }
            : mod.timing;
        ops.push({
            op: "set_modulator",
            index: mod.index,
            shape: mod.shape,
            timing,
            trigger: mod.trigger,
            ...(mod.phase ? { phase: mod.phase } : {}),
            ...(mod.fadeSeconds ? { fadeSeconds: mod.fadeSeconds } : {}),
            ...(mod.delaySeconds ? { delaySeconds: mod.delaySeconds } : {}),
            ...(mod.stereoOffset ? { stereoOffset: mod.stereoOffset } : {}),
        });
    }
    // Emit legacy lfo_shape for indexes that only have preset shapes and were not
    // already covered — kept for older tooling that only understands lfo_shape.
    // Prefer set_modulator as the canonical form; skip redundant lfo_shape.
    for (const osc of [1, 2, 3]) {
        ops.push({ op: "wavetable", osc, generator: model.wavetables[osc] });
    }
    const sequences = [...(model.sequences ?? [])].sort((a, b) => a.index - b.index);
    for (const seq of sequences) {
        ops.push({
            op: "sequence",
            index: seq.index,
            division: seq.division,
            steps: seq.steps,
            ...(seq.lanes.length > 0 ? { lanes: seq.lanes } : {}),
            retrigger: seq.retrigger,
            ...(seq.swing ? { swing: seq.swing } : {}),
        });
    }
    const macros = [...(model.macros ?? [])].sort((a, b) => a.index - b.index);
    for (const macro of macros) {
        if (macro.name === `MACRO ${macro.index}` && macro.assignments.length === 0)
            continue;
        ops.push({
            op: "set_macro",
            index: macro.index,
            name: macro.name,
            assignments: macro.assignments,
        });
    }
    const mods = [...model.modulations]
        .filter((m) => m.ownedBySequence == null)
        .sort((a, b) => a.slot - b.slot);
    for (const mod of mods) {
        ops.push({
            op: "modulate",
            slot: mod.slot,
            source: mod.source,
            destination: mod.destination,
            amount: mod.amount,
            ...(mod.bipolar ? { bipolar: true } : {}),
        });
    }
    if (ops.length > MAX_PATCH_OPS) {
        throw new Error(`modelToOps produced ${ops.length} ops (max ${MAX_PATCH_OPS})`);
    }
    return ops;
}
//# sourceMappingURL=model-to-ops.js.map